import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { EmptyState, InlineError, LoadingState } from "../components/States";
import { useToast } from "../components/Toast";
import { adminApi } from "../lib/api";
import type { InquiryRecord, InquiryStatus } from "../types";

const STATUSES: Array<{ value: InquiryStatus; label: string }> = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In progress" },
  { value: "responded", label: "Responded" },
  { value: "closed", label: "Closed" },
  { value: "spam", label: "Spam" }
];

interface InquiryPage {
  items: InquiryRecord[];
  nextCursor?: string;
}

function normalizeInquiry(payload: unknown, index: number): InquiryRecord {
  const source =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>)
      : {};
  return {
    ...source,
    id: String(source.id || source.reference || source.referenceNumber || index)
  };
}

function normalizePage(payload: unknown): InquiryPage {
  if (Array.isArray(payload)) {
    return { items: payload.map(normalizeInquiry) };
  }
  if (!payload || typeof payload !== "object") return { items: [] };
  const record = payload as Record<string, unknown>;
  const rawItems = Array.isArray(record.items)
    ? record.items
    : Array.isArray(record.inquiries)
      ? record.inquiries
      : Array.isArray(record.data)
        ? record.data
        : [];
  const pagination =
    record.pagination && typeof record.pagination === "object"
      ? (record.pagination as Record<string, unknown>)
      : {};
  const cursor =
    record.nextCursor || pagination.nextCursor || record.cursor || undefined;
  return {
    items: rawItems.map(normalizeInquiry),
    nextCursor:
      typeof cursor === "string" && cursor.trim() ? cursor : undefined
  };
}

function statusLabel(status: unknown): string {
  return (
    STATUSES.find((item) => item.value === status)?.label ||
    (typeof status === "string" ? status : "New")
  );
}

function formatDate(value: unknown): string {
  if (typeof value !== "string" || !Number.isFinite(Date.parse(value))) return "—";
  return new Intl.DateTimeFormat("en", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}

function timestampFor(inquiry: InquiryRecord): unknown {
  return inquiry.createdAt || inquiry.submittedAt || inquiry.timestamp;
}

function referenceFor(inquiry: InquiryRecord): string {
  return String(
    inquiry.reference ||
      inquiry.referenceNumber ||
      inquiry.id ||
      "No reference"
  );
}

function titleFor(inquiry: InquiryRecord): string {
  const company = inquiry.company || inquiry.companyName || inquiry.organization;
  const person = inquiry.name || inquiry.fullName || inquiry.contactName;
  return String(company || person || "Unnamed inquiry");
}

function secondaryFor(inquiry: InquiryRecord): string {
  const email = inquiry.email || inquiry.contactEmail;
  const subject = inquiry.subject || inquiry.product || inquiry.requirement;
  return String(email || subject || referenceFor(inquiry));
}

function displayField(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map(displayField).join(", ");
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, child]) => `${humanize(key)}: ${displayField(child)}`)
      .join(" · ");
  }
  return String(value);
}

function humanize(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}

function InquiryDetail({
  inquiry,
  onUpdated
}: {
  inquiry: InquiryRecord;
  onUpdated: (inquiry: InquiryRecord) => void;
}) {
  const { showToast } = useToast();
  const validStatus = STATUSES.some((item) => item.value === inquiry.status)
    ? (inquiry.status as InquiryStatus)
    : "new";
  const [status, setStatus] = useState<InquiryStatus>(validStatus);
  const [notes, setNotes] = useState(
    typeof inquiry.notes === "string" ? inquiry.notes : ""
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStatus(
      STATUSES.some((item) => item.value === inquiry.status)
        ? (inquiry.status as InquiryStatus)
        : "new"
    );
    setNotes(typeof inquiry.notes === "string" ? inquiry.notes : "");
  }, [inquiry]);

  const email =
    typeof inquiry.email === "string"
      ? inquiry.email
      : typeof inquiry.contactEmail === "string"
        ? inquiry.contactEmail
        : "";

  const submittedFields = Object.entries(inquiry).filter(
    ([key]) => !["notes", "status"].includes(key)
  );
  const changed = status !== validStatus || notes !== (inquiry.notes || "");

  async function save() {
    setSaving(true);
    try {
      await adminApi.updateInquiry(inquiry.id, { status, notes });
      onUpdated({ ...inquiry, status, notes });
      showToast("Inquiry updated.", "success");
    } catch (requestError) {
      showToast(
        requestError instanceof Error
          ? requestError.message
          : "The inquiry could not be updated.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="inquiry-detail" aria-labelledby="inquiry-detail-title">
      <header className="inquiry-detail__header">
        <div>
          <p className="eyebrow">Reference {referenceFor(inquiry)}</p>
          <h2 id="inquiry-detail-title">{titleFor(inquiry)}</h2>
          <p>{formatDate(timestampFor(inquiry))}</p>
        </div>
        {email && (
          <a className="button" href={`mailto:${email}`}>
            Email contact
          </a>
        )}
      </header>

      <section className="inquiry-workflow" aria-labelledby="workflow-title">
        <h3 id="workflow-title">Workflow</h3>
        <div className="field">
          <label htmlFor={`inquiry-status-${inquiry.id}`}>Status</label>
          <select
            id={`inquiry-status-${inquiry.id}`}
            value={status}
            onChange={(event) => setStatus(event.target.value as InquiryStatus)}
          >
            {STATUSES.map((item) => (
              <option value={item.value} key={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`inquiry-notes-${inquiry.id}`}>Internal notes</label>
          <textarea
            id={`inquiry-notes-${inquiry.id}`}
            rows={5}
            value={notes}
            placeholder="Add context, next steps, or a private handoff note."
            onChange={(event) => setNotes(event.target.value)}
          />
          <p className="field__help">
            Notes stay in the admin console and are not sent to the contact.
          </p>
        </div>
        <button
          type="button"
          className="button button--primary"
          disabled={!changed || saving}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save inquiry"}
        </button>
      </section>

      <section className="inquiry-fields" aria-labelledby="submitted-fields-title">
        <h3 id="submitted-fields-title">Submitted information</h3>
        <dl>
          {submittedFields.map(([key, value]) => (
            <div key={key}>
              <dt>{humanize(key)}</dt>
              <dd>
                {key.toLowerCase().includes("email") &&
                typeof value === "string" ? (
                  <a href={`mailto:${value}`}>{value}</a>
                ) : key.toLowerCase().includes("url") &&
                  typeof value === "string" ? (
                  <a href={value} target="_blank" rel="noreferrer">
                    {value}
                  </a>
                ) : (
                  displayField(value)
                )}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </article>
  );
}

export function InquiriesPage() {
  const [items, setItems] = useState<InquiryRecord[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [status, setStatus] = useState<InquiryStatus | "">("");
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) || null,
    [items, selectedId]
  );

  const load = useCallback(
    async (cursor?: string) => {
      cursor ? setLoadingMore(true) : setLoading(true);
      setError("");
      try {
        const page = normalizePage(await adminApi.inquiries(status, 30, cursor));
        setItems((current) => (cursor ? [...current, ...page.items] : page.items));
        setNextCursor(page.nextCursor);
        if (!cursor) {
          setSelectedId((current) =>
            page.items.some((item) => item.id === current)
              ? current
              : page.items[0]?.id || ""
          );
        }
      } catch (requestError) {
        setError(
          requestError instanceof Error
            ? requestError.message
            : "Inquiries could not be loaded."
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [status]
  );

  useEffect(() => {
    void load();
  }, [load]);

  function updateLocal(updated: InquiryRecord) {
    setItems((current) =>
      current.map((item) => (item.id === updated.id ? updated : item))
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Business requests"
        title="Inquiries"
        description="Review every submitted field, record internal context, and keep each request moving."
        actions={
          <button type="button" className="button" onClick={() => void load()}>
            Refresh
          </button>
        }
      />

      <div className="filter-bar">
        <div className="field">
          <label htmlFor="inquiry-filter">Filter by status</label>
          <select
            id="inquiry-filter"
            value={status}
            onChange={(event) =>
              setStatus(event.target.value as InquiryStatus | "")
            }
          >
            <option value="">All statuses</option>
            {STATUSES.map((item) => (
              <option value={item.value} key={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <span>{items.length} loaded</span>
      </div>

      {loading ? (
        <LoadingState label="Loading inquiries" />
      ) : error && items.length === 0 ? (
        <InlineError message={error} retry={() => void load()} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No inquiries found"
          message={
            status
              ? `There are no inquiries marked “${statusLabel(status)}”.`
              : "New business inquiries will appear here."
          }
          action={
            status ? (
              <button type="button" className="button" onClick={() => setStatus("")}>
                Show all statuses
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="inquiry-layout">
          <section className="inquiry-list" aria-label="Inquiry list">
            <ul>
              {items.map((inquiry) => (
                <li key={inquiry.id}>
                  <button
                    type="button"
                    className={selectedId === inquiry.id ? "is-active" : ""}
                    aria-pressed={selectedId === inquiry.id}
                    onClick={() => setSelectedId(inquiry.id)}
                  >
                    <span className="inquiry-list__topline">
                      <strong>{titleFor(inquiry)}</strong>
                      <span
                        className={`status-badge status-badge--${inquiry.status || "new"}`}
                      >
                        {statusLabel(inquiry.status)}
                      </span>
                    </span>
                    <span>{secondaryFor(inquiry)}</span>
                    <small>
                      {referenceFor(inquiry)} · {formatDate(timestampFor(inquiry))}
                    </small>
                  </button>
                </li>
              ))}
            </ul>
            {error && <InlineError message={error} />}
            {nextCursor && (
              <button
                type="button"
                className="button button--wide"
                disabled={loadingMore}
                onClick={() => void load(nextCursor)}
              >
                {loadingMore ? "Loading…" : "Load more inquiries"}
              </button>
            )}
          </section>
          {selected ? (
            <InquiryDetail
              key={selected.id}
              inquiry={selected}
              onUpdated={updateLocal}
            />
          ) : (
            <EmptyState
              title="Select an inquiry"
              message="Choose a request to review its submitted information."
            />
          )}
        </div>
      )}
    </>
  );
}
